import { useEffect, useRef } from 'react';
import useLocalStorage, {
  writeStorage,
  deleteFromStorage,
} from '@rehooks/local-storage';

const EXTERNAL_WRITE_POLL_MS = 500;

/**
 * Read a localStorage string that may be written by a host app.
 *
 * `useLocalStorage` already reacts to cross-tab `storage` events and to
 * `writeStorage`/`deleteFromStorage`. Neither fires when a host writes the key
 * with a raw `localStorage.setItem` in this same document — the case behind
 * Revenue's multi-role dialog writing `auth_userrole`. Poll for that, then
 * re-broadcast through the library so every subscriber of the key stays in sync.
 *
 * The library is used purely as the re-render subscription; the value returned
 * is the raw stored string, which is what consumers such as NavigationAdapter
 * read directly. Going through the library's parsed form instead would let a
 * JSON-parseable value (`"Approver"`, `{...}`) compare equal here while the
 * adapter still sees a different string.
 */
export function useLocalStorageValue(
  key: string,
  enabled = true,
): string | null {
  useLocalStorage<string>(key);

  const lastRawValue = useRef<string | null>(null);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    // The library read this key when it subscribed, so seed rather than
    // broadcast — otherwise every mount writes the key back to storage.
    lastRawValue.current = window.localStorage.getItem(key);

    const syncExternalWrite = () => {
      const raw = window.localStorage.getItem(key);
      if (raw === lastRawValue.current) {
        return;
      }
      lastRawValue.current = raw;

      if (raw === null) {
        deleteFromStorage(key);
      } else {
        writeStorage(key, raw);
      }
    };

    const intervalId = window.setInterval(
      syncExternalWrite,
      EXTERNAL_WRITE_POLL_MS,
    );

    return () => window.clearInterval(intervalId);
  }, [key, enabled]);

  return enabled ? window.localStorage.getItem(key) : null;
}
